"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.IGTradeUtils = void 0;
const react_1 = require("react");
const decimal_js_1 = __importDefault(require("decimal.js"));
const trade_utils_lib_1 = require("trade_utils_lib");
const TradeErrorConstants_1 = require("./TradeErrorConstants");
exports.IGTradeUtils = {
    useIGAIStrategy(base, quote, tradeApi, market) {
        const [item, setItem] = (0, react_1.useState)(undefined);
        const [loading, setLoading] = (0, react_1.useState)(true);
        const [error, setError] = (0, react_1.useState)("");
        (0, react_1.useEffect)(() => {
            setLoading(true);
            (0, trade_utils_lib_1.requestWithRetry)(() => tradeApi.requireIGAIStrategy(market, base, quote), { maxRetryCount: 3 }).subscribe({
                next: (data) => {
                    const aiItem = data;
                    if (aiItem.low || aiItem.reward) {
                        setItem(aiItem);
                    }
                    else {
                        setItem(undefined);
                    }
                    setError("");
                    setLoading(false);
                },
                error: (error) => {
                    setItem(undefined);
                    setError(JSON.stringify(error) || `${error}`);
                    setLoading(false);
                }
            });
        }, [base, quote]);
        return [item, loading, error];
    },
    checkBalanceEnough(symbol, invest, bottom, rate, baseAmount, quoteAmount, tradeApi, market, investType = "both", conditional) {
        let baseAvaliable = baseAmount;
        let quoteAvaliable = quoteAmount;
        if (investType === "base") {
            quoteAvaliable = "0";
        }
        else if (investType === "quote") {
            baseAvaliable = "0";
        }
        const params = {
            exchange: market,
            base: symbol.base,
            quote: symbol.quote,
            invest: invest,
            bottom: bottom,
            rate: rate,
            base_amount: baseAvaliable,
            quote_amount: quoteAvaliable,
            trigger_price: conditional || null,
            market_info: {
                id: `${symbol.base}_${symbol.quote}`,
                baseId: symbol.base,
                quoteId: symbol.quote,
                symbol: symbol.symbol,
                base: symbol.base,
                quote: symbol.quote,
                precision: {
                    amount: symbol.amount,
                    price: symbol.precision,
                    cost: symbol.precisionCost,
                },
                limits: symbol.limit,
            },
        };
        return (0, trade_utils_lib_1.requestWithRetry)(() => tradeApi.checkIGBalanceEnough(params), {
            maxRetryCount: 3,
        });
    },
    checkCreateParams(symbol, invest, bottom, rate, totalBalance, fee, tradeApi, $st) {
        const investNum = trade_utils_lib_1.NumberCommonUtils.formatterDecimal(invest, symbol.precision).toNumber();
        const bottomNum = trade_utils_lib_1.NumberCommonUtils.formatterDecimal(bottom, symbol.precision).toNumber();
        const rateNum = trade_utils_lib_1.NumberCommonUtils.str2number(rate);
        if (!bottomNum) {
            return TradeErrorConstants_1.TradeErrorConstants.inputLow($st);
        }
        if (!rateNum) {
            return TradeErrorConstants_1.TradeErrorConstants.inputGridPerProfitRate($st);
        }
        if (!investNum) {
            return TradeErrorConstants_1.TradeErrorConstants.inputInvest($st);
        }
        if (rateNum < fee * 2 || rateNum > 0.1) {
            const min = trade_utils_lib_1.NumberCommonUtils.toPercent(fee * 2 * 100, false);
            return TradeErrorConstants_1.TradeErrorConstants.igTradingRateRangeWrong(min, $st);
        }
        if (investNum > totalBalance) {
            return TradeErrorConstants_1.TradeErrorConstants.balanceNotEnough($st);
        }
        return "";
    },
    useMinInvest(symbol, bottom, rate, tradeApi, market) {
        const [result, setResult] = (0, react_1.useState)(0);
        const [loading, setLoading] = (0, react_1.useState)(false);
        const [error, setError] = (0, react_1.useState)("");
        (0, react_1.useEffect)(() => {
            setResult(0);
            if (!trade_utils_lib_1.NumberCommonUtils.str2number(bottom) || !rate) {
                return;
            }
            if (rate < 0.001 || rate > 0.1) {
                return;
            }
            setLoading(true);
            const subscribtion = this.checkBalanceEnough(symbol, "0", bottom, `${rate}`, "0", "0", tradeApi, market).subscribe({
                next: (data) => {
                    setResult(trade_utils_lib_1.NumberCommonUtils.formatterDecimal(data.invest_min || 0, symbol.precision, decimal_js_1.default.ROUND_CEIL).toNumber());
                    setLoading(false);
                    setError("");
                },
                error: (error) => {
                    setResult(0);
                    setLoading(false);
                    setError(error);
                }
            });
            return () => {
                if (subscribtion) {
                    subscribtion.unsubscribe();
                }
                setLoading(false);
            };
        }, [symbol, bottom, rate, tradeApi]);
        return [result, loading, error];
    },
    useCreateOrderError(symbol, bottom, rate, invest, min, fee, $st) {
        return (0, react_1.useMemo)(() => {
            const bottomNum = trade_utils_lib_1.NumberCommonUtils.formatterDecimal(bottom, symbol.precision).toNumber();
            const investNum = trade_utils_lib_1.NumberCommonUtils.formatterDecimal(invest, symbol.precision).toNumber();
            if (!bottomNum && !rate && !investNum) {
                return "";
            }
            if (rate && (rate > 0.1 || rate < fee * 2)) {
                const min = trade_utils_lib_1.NumberCommonUtils.toPercent(fee * 2 * 100, false);
                return TradeErrorConstants_1.TradeErrorConstants.igTradingRateRangeWrong(min, $st);
            }
            if (investNum && min && investNum < min) {
                return $st("IG_INSUFFICIENT_INVEST_ERROR", { min: min });
            }
            return "";
        }, [bottom, rate, invest, min, $st, fee]);
    },
    translateIGErr(result, symbol, tradeApi, $st) {
        switch (result.code) {
            case "BAD_STRATEGY_PARAMETERS":
                return $st("BAD_STRATEGY_PARAMETERS");
            case "INSUFFICIENT_INVEST":
                // 投资额不足
                let minStr = "unknown";
                if (result.invest_min) {
                    minStr =
                        trade_utils_lib_1.NumberCommonUtils.formatterDecimalToStr(result.invest_min, symbol.precision, decimal_js_1.default.ROUND_CEIL) +
                            " " +
                            symbol.quote;
                }
                return $st("IG_INSUFFICIENT_INVEST_ERROR", { min: minStr });
            case "INSUFFICIENT_FUNDS":
                // 资金不足
                const needBalance = trade_utils_lib_1.NumberCommonUtils.formatterDecimalToStr(result.base_allocate * result.price + result.quote_allocate, symbol.precision);
                return $st("IG_INSUFFICIENT_FUNDS_ERROR", {
                    min: `${needBalance} ${symbol.quote}`,
                });
            case "BAD_BALANCE_ORDER":
                // 无效平衡单
                return $st("IG_BAD_BALANCE_ORDER");
            case "NO_PRICE":
                return $st("trade_create_order_get_price_failed");
            case "OUT_OF_BOUNDS":
                return $st("ig_trading_out_of_bounds_error");
            case "NO_ORDER_BOOK":
                // 买卖盘获取失败
                return $st("trade_create_order_get_order_book_failed");
            case "UNKNOWN_ERROR":
                // 未知错误
                return TradeErrorConstants_1.TradeErrorConstants.gridUnknownError($st);
            default:
                const decodeError = result.code || JSON.stringify(result) || `${result}`;
                return $st("trade_grid_error_unknown") + `: ${decodeError}`;
        }
    },
};
